<template>
  <Table
    id="curator_suppliers_packages_list_table"
    ref="table"
    category="Packages"
    :items="packages"
    :columns="columns"
    :expand="[
      'package_properties',
      'package_external_exchange_restrictions',
      'inventory_targeting.co2e_emissions.score',
    ]"
    :fixed-filters="fixedFilters"
    :show-filters="true"
    :total-count="totalCount"
    :is-loading="isLoading"
    :text-search-properties="['package_id', 'deal_id', 'name', 'package_properties.value']"
    pagination
    :allow-selection="allowSelection"
    :allow-selection-all="discoveredDeals.length > 0"
    :can-select-item="canSelectItem"
    :selection-tooltip-message="selectionTooltipMessage"
    :reset-selection-on-update="false"
    @filters-changed="onTableFiltersChanged"
    @selection-changed="onSelectionChanged">
    <template #link="{ item: deal }">
      <button
        v-tooltip="
          capitalize(
            $t(activeDeal?.dealID === deal?.dealID ? 'messages.closeDealDetailsPane' : 'messages.showTargetingDetails'),
          )
        "
        data-testid="request-deal-details-button"
        class="btn btn-no-text btn-xxs btn-info-hollow mr-3"
        @click="emit('requestedDealDetails', deal)">
        <icon-eye
          v-if="activeDeal?.dealID !== deal?.dealID"
          class="icon" />
        <icon-close
          v-else
          class="icon" />
      </button>
      <router-link
        :to="deal.endpoint"
        class="entity-link"
        style="margin-right: 0.6rem"
        :title="deal.name"
        @mouseenter="onDealLinkMouseEnter(deal)"
        @mouseleave="onDealLinkMouseLeave(deal)">
        {{ deal.name }}
      </router-link>
      <span class="icon">
        <icon-colored-private-deal
          v-if="deal.isPrivate"
          class="icon" />
      </span>
    </template>
    <template #mediaOwner="{ item: deal }">
      <img
        v-if="getMediaOwner(deal.organizationID)?.logoUrl != null"
        v-tooltip="getMediaOwner(deal.organizationID).name"
        :src="getMediaOwner(deal.organizationID).logoUrl"
        :alt="getMediaOwner(deal.organizationID).name" />
      <span v-else-if="getMediaOwner(deal.organizationID) !== undefined">
        {{ getMediaOwner(deal.organizationID).name }}
      </span>
    </template>
    <template #currency="{ item: deal }">
      {{ deal.currencyInformation.currency }}
    </template>
    <template #status="{ item: deal }">
      <package-status-icon :status="deal.state" />
      {{ capitalize(deal.status) }}
    </template>
    <template #co2EmissionsScore="{ item: deal }">
      <template v-if="deal.co2EmissionsScore > 0">
        <green-score
          style="width: 4.8rem; height: 2.4rem; font-size: 1.4rem"
          :is-animating="false"
          :show-green-leaf="false"
          :inline="true"
          :use-animations="false"
          :co2-score="deal.co2EmissionsScore" />
        <green-score
          v-if="deal.co2Score > 50"
          style="margin-left: 0.6rem; width: 2.4rem; height: 2.4rem; font-size: 1.4rem"
          :use-animations="false"
          :is-animating="false"
          :co2-score="deal.co2EmissionsScore" />
      </template>
      <span v-else>{{ DASH }}</span>
    </template>
    <template #createdBy="{ item: deal }">
      <UserDetails :user-i-d="deal.apiObject.created_by" />
    </template>

    <template #no-records>
      <no-records-table
        :icon="IconComplexDeal"
        :header-text="$t('messages.tablePlaceholderNoDealsTitleDemandSize')"
        :details-text="`${$t('messages.tablePlaceholderNoDealsDescriptionDemandSize')}`" />
    </template>

    <template #no-match>
      <no-records-table
        :icon="IconComplexDeal"
        :header-text="$t('messages.tablePlaceholderNoMatchingDealsTitle')"
        :details-text="`${$t('messages.tablePlaceholderNoMatchingItemsDescription')}`" />
    </template>
  </Table>
</template>

<script setup lang="ts">
  import { CanceledError } from 'axios';
  import { DateTime } from 'luxon';
  import { capitalize, computed, onMounted, Ref, ref, useTemplateRef, watch } from 'vue';
  import { ComponentExposed } from 'vue-component-type-helpers';

  import GreenScore from '@/common/components/controls/GreenScore.vue';
  import NoRecordsTable from '@/common/components/controls/table/NoRecordsTable.vue';
  import Table from '@/common/components/controls/table/Table.vue';
  import TableColumn from '@/common/components/controls/table/TableColumn';
  import TableColumnTypes from '@/common/components/controls/table/TableColumnTypes';
  import { DealScope, getDealScopeType } from '@/common/components/deals/DealsHelper';
  import PackageStatusIcon from '@/common/components/deals/PackageStatusIcon.vue';
  import { IconColoredPrivateDeal } from '@/common/icons/colored';
  import { IconComplexDeal } from '@/common/icons/complex';
  import { IconClose, IconEye } from '@/common/icons/monochrome';
  import { Package, QueryParams } from '@/common/models';
  import MediaOwner from '@/common/models/media-owner/MediaOwner';
  import Filter from '@/common/models/query-params/Filter';
  import * as FilterComparisonOperator from '@/common/models/query-params/FilterComparisonOperator';
  import ListFilter from '@/common/models/query-params/ListFilter';
  import ErrorHandler from '@/ErrorHandler';
  import { DASH } from '@/globals';
  import UserDetails from '@/org-app/components/users/UserDetails.vue';
  import { waitUntil } from '@/utils/DelayHelper';
  import { mapOrgAppActions, mapOrgAppState } from '@/utils/StateMapping';
  import i18n from '@/vueI18n';

  interface ICuratorSupplierDealsTableProps {
    activeDeal?: Package;
    allowSelection?: boolean;
    currency?: string;
    fixedFilters: Filter[];
    suppliers: MediaOwner[];
    discoveredDeals?: number[];
    dealLockedScopeType?: DealScope;
  }
  const props = withDefaults(defineProps<ICuratorSupplierDealsTableProps>(), {
    activeDeal: null,
    allowSelection: false,
    currency: null,
    discoveredDeals: () => [],
    dealLockedScopeType: null,
  });

  const emit = defineEmits([
    'dataChanged',
    'dealLinkMouseEnter',
    'dealLinkMouseLeave',
    'requestedDealDetails',
    'selectionChanged',
    'filtersChanged',
    'defaultFiltersLoaded',
    'onTableLoaded',
  ]);

  const totalCount = ref(0);
  const filterText: Ref<string> = ref(null);
  const table = useTemplateRef<ComponentExposed<typeof Table>>('table');
  const isLoading = ref(true);
  const isFirstLoad = ref(true);
  const packages: Ref<Package[]> = ref([]);
  const { organizationSettings, organization } = mapOrgAppState();
  let maxWaitTimeToLoadTableFilters = 500;

  const selectedFilters = computed(() => table.value?.selectedFilters);

  const columns = computed(() => {
    const allSupplierCountries = props.suppliers.map((x) => x.countries).flat();
    const uniqueSupplierCountries = [...new Set(allSupplierCountries)];
    const uniqueSupplierCurrencies = [...new Set(props.suppliers.map((x) => x.currency))];
    const currencyListOptions = uniqueSupplierCurrencies.map((x) => ({
      id: x,
      name: x,
    }));
    const countriesListOptions = uniqueSupplierCountries.map((x) => ({
      id: x,
      name: x,
    }));
    const columns: Record<string, TableColumn> = {
      link: Package.entityProperties.name.getTableColumn(),
    };

    columns.mediaOwner = Package.entityProperties.mediaOwner.getTableColumn({
      allowSort: false,
    });
    columns.co2EmissionsScore = TableColumn.fromObject({
      key: 'co2EmissionsScore',
      label: capitalize(i18n.global.t('names.co2EmissionsScore')).toUpperCase().replace('CO₂E', 'CO₂e'),
      property: 'co2EmissionsScore',
      allowSort: false,
      useAsFilter: false,
      shouldBeUppercased: false,
    });

    columns.dealIDFormatted = Package.entityProperties.dealIDFormatted.getTableColumn();
    columns.startDateDescription = Package.entityProperties.startDate.getTableColumn({
      orderBy: ['flight_from'],
    });
    columns.endDateDescription = Package.entityProperties.endDate.getTableColumn({
      orderBy: ['flight_to'],
    });
    columns.cpmDescription = Package.entityProperties.cpmDescription.getTableColumn({
      useAsFilter: false,
      orderBy: ['cpm_from_rate_card', 'cpm_fixed'],
    });
    columns.countries = TableColumn.fromObject({
      label: capitalize(i18n.global.t('names.country', { n: 1 })),
      property: 'countries',
      type: TableColumnTypes.list,
      source: countriesListOptions,
      sourceFilterProperty: 'countries',
      allowSort: false,
      isDefault: true,
    });
    columns.currency = TableColumn.fromObject({
      label: 'Currency',
      property: 'currency',
      type: TableColumnTypes.list,
      source: currencyListOptions,
      sourceFilterProperty: 'currency',
      allowSort: false,
      isDefault: true,
    });
    columns.cpmFixed = Package.entityProperties.cpmFixed.getTableColumn({
      shouldRenderColumn: false,
    });
    columns.dealVisibility = Package.entityProperties.dealVisibility.getTableColumn({
      property: 'curator_access',
      sourceFilterProperty: 'curator_access',
      shouldRenderColumn: false,
    });
    columns.modifiedOnFormatted = Package.entityProperties.modifiedOnFormatted.getTableColumn();
    columns.isHivestackDSP = TableColumn.fromObject({
      label: 'Available for',
      property: 'forBidding',
      sourceFilterProperty: 'for_bidding',
      type: TableColumnTypes.list,
      allowSort: false,
      shouldRenderColumn: false,
      allowExclusions: false,
      source: () => [
        { id: false, name: 'HS DSP' },
        { id: true, name: 'External DSP' },
      ],
      customQueryParams: (values) => {
        // Ensure that we include null since internal/external DSPs don't apply.
        return `(for_bidding in (${values.join(',')}) or for_bidding eq null)`;
      },
    });

    return columns;
  });

  const { getCuratorPackages } = mapOrgAppActions();

  function onTableFiltersChanged() {
    reloadPackages();
  }

  function setDefaultFilters(listFilter: ListFilter, selectedData: string[], tableFilter): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      try {
        if (listFilter) {
          listFilter.valuesInternal = selectedData;
          tableFilter.toggleOption({ excluded: [], included: selectedData.map((x) => ({ id: x, name: x })) }, false);
          tableFilter.commitListFilterChange({
            excluded: [],
            included: selectedData.map((x) => ({ id: x, name: x })),
          });
        }
        resolve();
      } catch (error) {
        reject(error);
      }
    });
  }

  async function reloadPackages() {
    await waitUntil(() => table.value != null);
    const countries = organization.countries || [];
    if (selectedFilters.value.length === 0 && isFirstLoad.value && maxWaitTimeToLoadTableFilters > 0) {
      maxWaitTimeToLoadTableFilters = maxWaitTimeToLoadTableFilters - 100;
      setTimeout(reloadPackages, 100);
      return;
    }
    if (isFirstLoad.value) {
      // On first load make the default selection of countries that are in SGC.
      const countryListFilter = selectedFilters.value.find((x) => x.property === 'countries')?.instance as ListFilter;

      const countriesTableFilter = table.value.tableFilters?.find(
        (tableFilter) => tableFilter.filter.property === 'countries',
      );

      const currencyListFilter = selectedFilters.value.find((x) => x.property === 'currency')?.instance as ListFilter;
      const currencyTableFilter = table.value.tableFilters?.find(
        (tableFilter) => tableFilter.filter.property === 'currency',
      );

      if (!countriesTableFilter && !currencyTableFilter && maxWaitTimeToLoadTableFilters > 0) {
        // sometimes filters lags few ms to load, so this is required
        setTimeout(reloadPackages, 100);
        return;
      }
      if (countryListFilter?.filter && currencyListFilter?.filter) {
        await setDefaultFilters(countryListFilter, countries, countriesTableFilter);
        await setDefaultFilters(currencyListFilter, [organization.currency], currencyTableFilter);
        emit('defaultFiltersLoaded', table.value.activeFilters);
        isFirstLoad.value = false;
      }
    }

    // Delay the call, sometimes the fixedFilters don't propagate immediately to table.queryParams.
    setTimeout(async () => {
      // Close the deal details pane.
      emit('requestedDealDetails', null);

      isLoading.value = true;
      const { queryParams } = table.value;
      queryParams.customQueryParams.$orgfilter = null;

      // Apply the org filters when applicable
      const rawOrgFilter = [
        ...buildFilters(
          queryParams,
          'countries',
          'organizationcountries.any(c:c.country_code in ({0}))',
          'organizationcountries.all(c:c.country_code ni ({0}))',
        ),
        ...buildFilters(queryParams, 'currency'),
      ];

      if (rawOrgFilter.length > 0) {
        queryParams.customQueryParams.$orgfilter = rawOrgFilter.join(' and ');
      }

      queryParams.showLoader = false;

      // Get all media owners and filter it with search term that matches organization name,
      // and pass the organization_id to get packages
      if (filterText.value != null) {
        const organizationIDs = props.suppliers
          .filter((mediaOwner) => mediaOwner.name.toLowerCase().includes(filterText.value))
          .map((mediaOwner) => mediaOwner.id);

        const { filters } = queryParams.filterCollection.filters.find((x) => x.type === 'FilterGroup') || {};

        if (organizationIDs.length > 0 && filters != null) {
          const newFilter = new Filter('organization_id', organizationIDs, FilterComparisonOperator.IN);
          filters.push(newFilter);
        }
      }

      if (props.discoveredDeals?.length > 0) {
        queryParams.filterCollection.filters.push(
          new Filter('package_id', props.discoveredDeals, FilterComparisonOperator.IN),
        );
      }

      try {
        const response = await queryParams.run(getCuratorPackages);
        if (response) {
          packages.value = response.items.map((p) => {
            p.mediaOwner = props.suppliers.find((s) => s.id === p.organizationID);
            return p;
          });
          totalCount.value = response.totalCount;
          isLoading.value = false;
        }
        isFirstLoad.value = false;
      } catch (e) {
        if (!(e instanceof CanceledError)) isLoading.value = false;
        ErrorHandler.handleExceptionInUIComponent(e);
      }
    }, 100);
  }

  function getMediaOwner(id: number): MediaOwner {
    return props.suppliers.find((x) => x.id === id);
  }

  function onSelectionChanged(selection) {
    emit('selectionChanged', selection);
  }

  function onDealLinkMouseEnter(deal: Package) {
    emit('dealLinkMouseEnter', deal);
  }

  function onDealLinkMouseLeave(deal: Package) {
    emit('dealLinkMouseLeave', deal);
  }

  /**
   * Generate raw filter queries for the given property based on the filters in the queryParams.
   * To be used when manually manipulating the queryParams.
   * @param queryParams
   * @param property
   * @param customInclude Custom template for the IN filter. Use {0} as a placeholder for the values.
   * @param customExclude Custom template for the NI filter. Use {0} as a placeholder for the values.
   */
  function buildFilters(
    queryParams: QueryParams,
    property: string,
    customInclude: string = null,
    customExclude: string = null,
  ) {
    const filters = queryParams.filterCollection.filters.filter((x) => x.property === property);
    if (filters.length == 0) return [];
    const rawFilters = [];
    queryParams.filterCollection.filters = queryParams.filterCollection.filters.filter((x) => x.property !== property);
    filters.forEach((filter) => {
      const { comparisonOperator, values } = filter;
      if ([FilterComparisonOperator.IN, FilterComparisonOperator.EQUAL].includes(comparisonOperator)) {
        rawFilters.push(
          customInclude ? fillTemplate(customInclude, values) : `${property} in (${values.map((x) => `'${x}'`)})`,
        );
      } else if (comparisonOperator === FilterComparisonOperator.NOT_IN) {
        rawFilters.push(
          customExclude ? fillTemplate(customExclude, values) : `${property} ni (${values.map((x) => `'${x}'`)})`,
        );
      }
    });
    return rawFilters;
  }

  function fillTemplate(template: string, values: string[]) {
    return template.replace(/\{0}/g, () => values.map((x) => `'${x}'`).join(','));
  }

  // Modify the value of the currency filter and disable it if the currency is locked
  async function modifyCurrencyFilter(inherit: boolean = true) {
    await waitUntil(() => table.value != null);
    let currencyFilter = table.value.activeFilters.find((x) => x.property === 'currency');
    if (!currencyFilter && !inherit) return;
    if (!currencyFilter) {
      currencyFilter = table.value.availableFilters.find((x) => x.property === 'currency');
      currencyFilter.instance = await table.value.getFilterInstance(currencyFilter);
      table.value.selectFilters([...table.value.selectedFilters, currencyFilter]);
    }
    if (inherit) {
      table.value.queryParams.skip = 0;
      currencyFilter.instance.values = [props.currency];
      // Trigger the filter reload
      currencyFilter.instance.valuesChangedExternallyAt = DateTime.now();
      table.value.fireFiltersChanged();
    }
    currencyFilter.isDisabled = inherit;
  }

  async function modifyDealScopeFilter(dealLockedScopeType: DealScope) {
    await waitUntil(() => table.value != null);
    // Verify if filter already exists, if so access it, if not create it and add it to the selected filters
    let dealScopeFilter = table.value.activeFilters.find((x) => x.property === 'forBidding');
    if (!dealScopeFilter) {
      dealScopeFilter = table.value.availableFilters.find((x) => x.property === 'forBidding');
      dealScopeFilter.instance = await table.value.getFilterInstance(dealScopeFilter);
      table.value.selectFilters([...table.value.selectedFilters, dealScopeFilter]);
    }
    dealScopeFilter.isDisabled = dealLockedScopeType != null;
    if (dealLockedScopeType == null) return;
    // Get the filter instance so that we can update the values reactively
    if (dealLockedScopeType === DealScope.INTERNAL) {
      dealScopeFilter.instance.values = [false];
    } else if (dealLockedScopeType === DealScope.EXTERNAL) {
      dealScopeFilter.instance.values = [true];
    }
    // Trigger the filter reload (this will update the values on the UI in the case where we aren't creating a new filter instance)
    dealScopeFilter.instance.valuesChangedExternallyAt = DateTime.now();
    table.value.fireFiltersChanged();
  }

  function selectionTooltipMessage(item: Package): string | null {
    if (item.directBilling && !organizationSettings.curateAllowDirectBillingDeals) {
      return i18n.global.t('messages.curatedDealsCannotIncludeDirectBillingPublisherDeals');
    }
    return null;
  }

  function canSelectItem(item: Package, selectedItems: Package[]) {
    if (item.directBilling && !organizationSettings.curateAllowDirectBillingDeals) {
      return false;
    }
    const itemScopeType = getDealScopeType(item.isForBidding);
    // Ensures that deals are only compatible with the same scope type or if for_bidding is null
    let isDealScopeCompatible =
      props.dealLockedScopeType == null || props.dealLockedScopeType === itemScopeType || itemScopeType == null;
    if (selectedItems.length === 0) {
      return isDealScopeCompatible;
    }
    // Validate that any previously selected deal is compatible with the current item
    const prevSelectedItem = selectedItems.find((i) => i.isForBidding != null) || selectedItems[0];
    // Update the scope compatibility flag to incorporate the previously selected item (In cases where we aren't strictly locking the filter)
    const prevSelectedItemScopeType = getDealScopeType(prevSelectedItem.isForBidding);
    isDealScopeCompatible =
      isDealScopeCompatible &&
      (prevSelectedItemScopeType == null || itemScopeType == null || prevSelectedItemScopeType === itemScopeType);
    // Only allow selection of deals with the same currency as the selected deals
    const hasSameCurrency = item.currencyInformation.currency === prevSelectedItem.currencyInformation.currency;
    return hasSameCurrency && isDealScopeCompatible;
  }

  watch(
    () => table.value,
    () => {
      if (table.value) emit('onTableLoaded', table.value);
    },
  );

  watch(() => props.fixedFilters, reloadPackages);

  watch(
    () => props.currency,
    async (newValue, oldValue) => {
      if (newValue === null) {
        await modifyCurrencyFilter(false);
        await reloadPackages();
      } else if (newValue !== oldValue) {
        await modifyCurrencyFilter();
      }
    },
    { immediate: true },
  );

  watch(
    () => selectedFilters.value,
    (newFilters) => {
      if (!newFilters) newFilters = [];

      const countryFilters = newFilters.find((x) => x.property === 'countries')?.instance;
      const currencyFilters = newFilters.find((x) => x.property === 'currency')?.instance;

      // Emit the filters to the parent component to propagate changes to EditDeal
      const filterChanges = {
        countries: {
          included: new Set(countryFilters?.valuesInternal ?? []),
          excluded: new Set(countryFilters?.excludedValuesInternal ?? []),
        },
        currency: {
          included: new Set(currencyFilters?.valuesInternal ?? []),
          excluded: new Set(currencyFilters?.excludedValuesInternal ?? []),
        },
      };

      emit('filtersChanged', filterChanges);
    },
  );

  watch(
    () => props.dealLockedScopeType,
    async (newValue, oldValue) => {
      if (newValue == oldValue) return;
      await modifyDealScopeFilter(newValue);
    },
    { immediate: true },
  );

  defineExpose({
    reloadPackages,
    table,
  });

  onMounted(() => {
    reloadPackages();
  });
</script>

<style scoped lang="scss">
  [data-key='cell-wrapper-mediaOwner'] {
    img {
      max-width: 10rem;
      max-height: 3rem;
    }
  }
</style>
